import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { InvitationService } from '../services/invitationService';
import type { Invitation } from '../services/invitationService';

const shellClassName = 'auth-screen min-h-screen flex items-center justify-center p-4';
const cardClassName = 'auth-card w-full max-w-md rounded p-8';
const inputClassName = 'auth-input w-full rounded px-4 py-2 text-sm';
const labelClassName = 'auth-label mb-1 block text-left text-xs font-medium';

type AuthMode = 'signup' | 'login';

export default function InviteAcceptPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [invitation, setInvitation] = useState<Invitation | null>(null);

  // Auth state
  const [currentUserEmail, setCurrentUserEmail] = useState<string | null>(null);

  // Registration/login form
  const [authMode, setAuthMode] = useState<AuthMode>('signup');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const doAccept = useCallback(async () => {
    if (!token) return false;
    const result = await InvitationService.accept(token);
    if (!result.success) {
      setError(result.error ?? 'Erro ao aceitar convite');
      return false;
    }
    setSuccess(true);
    setTimeout(() => {
      navigate(result.project_id ? `/projetos/${result.project_id}` : '/projetos');
    }, 2000);
    return true;
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      setError('Token de convite não encontrado na URL');
      setLoading(false);
      return;
    }
    void loadInvitation();
  }, [token]);

  // When the correct user is logged in and invitation is loaded, auto-accept
  // (handles the case where they just confirmed their email and were redirected back)
  useEffect(() => {
    if (!invitation || !currentUserEmail) return;
    if (currentUserEmail !== invitation.email) return;
    // Check if the URL has a Supabase auth hash (email confirmation redirect)
    const hasAuthCallback = window.location.hash.includes('access_token') ||
      new URLSearchParams(window.location.search).get('type') === 'signup';
    if (hasAuthCallback) {
      void doAccept();
    }
  }, [invitation, currentUserEmail, doAccept]);

  async function loadInvitation() {
    try {
      setLoading(true);
      setError('');

      console.log('[InviteAcceptPage] carregando convite, token:', token);

      const { data: { session } } = await supabase.auth.getSession();
      setCurrentUserEmail(session?.user?.email ?? null);
      console.log('[InviteAcceptPage] usuário atual:', session?.user?.email ?? 'não logado');

      const inv = await InvitationService.getByToken(token!);
      console.log('[InviteAcceptPage] convite retornado:', inv);
      if (!inv) { setError('Convite não encontrado'); return; }
      if (inv.status === 'accepted') { setError('Este convite já foi aceito'); return; }
      if (inv.status === 'expired' || new Date(inv.expires_at) < new Date()) {
        setError('Este convite expirou'); return;
      }
      if (inv.status === 'revoked') { setError('Este convite foi revogado'); return; }

      setInvitation(inv);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao validar convite');
    } finally {
      setLoading(false);
    }
  }

  // Case: correct user already logged in
  const handleAcceptAsCurrentUser = useCallback(async () => {
    setSubmitting(true);
    setError('');
    try {
      await doAccept();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao aceitar convite');
    } finally {
      setSubmitting(false);
    }
  }, [doAccept]);

  // Case: sign out current user to use invite email
  const handleSwitchAccount = useCallback(async () => {
    await supabase.auth.signOut();
    // Do NOT navigate — stay on this page. Auth state update will cause re-render.
    setCurrentUserEmail(null);
  }, []);

  // Case: new user signing up via invite
  const handleSignUp = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invitation) return;
    if (!fullName.trim()) { setError('Informe seu nome completo'); return; }
    if (password.length < 8) { setError('A senha deve ter no mínimo 8 caracteres'); return; }
    if (password !== confirmPassword) { setError('As senhas não coincidem'); return; }

    setSubmitting(true);
    setError('');
    try {
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
      const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

      // Call Edge Function to create user server-side (pre-confirmed, no confirmation email)
      const res = await fetch(`${SUPABASE_URL}/functions/v1/accept-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${ANON_KEY}`,
        },
        body: JSON.stringify({ token, fullName, password }),
      });

      const json = await res.json();

      if (!res.ok) {
        throw new Error(json.error || 'Erro ao criar conta');
      }

      // Account created + invitation accepted server-side — now log the user in
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: invitation.email,
        password,
      });

      if (signInError) throw signInError;

      setSuccess(true);
      setTimeout(() => {
        navigate(json.project_id ? `/projetos/${json.project_id}` : '/projetos');
      }, 1500);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao criar conta');
    } finally {
      setSubmitting(false);
    }
  }, [invitation, fullName, password, confirmPassword, token, navigate]);

  // Case: existing user logging in to accept invite
  const handleLogin = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invitation) return;
    if (!password) { setError('Informe sua senha'); return; }

    setSubmitting(true);
    setError('');
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: invitation.email,
        password,
      });

      if (signInError) {
        setError('Senha incorreta ou conta não encontrada para este e-mail');
        return;
      }

      await doAccept();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao fazer login');
    } finally {
      setSubmitting(false);
    }
  }, [invitation, password, doAccept]);

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={shellClassName}>
        <div className={cardClassName + ' text-center'}>
          <div className="animate-pulse text-slate-500">Validando convite...</div>
        </div>
      </div>
    );
  }

  // ── Invalid token ────────────────────────────────────────────────────────
  if (error && !invitation) {
    return (
      <div className={shellClassName}>
        <div className={cardClassName + ' text-center'}>
          <div className="mb-4 text-3xl">⚠️</div>
          <h1 className="mb-2 text-lg font-semibold text-slate-800 dark:text-slate-100">Convite Inválido</h1>
          <p className="mb-6 text-sm text-slate-500">{error}</p>
          <button onClick={() => navigate('/')} className="rounded bg-blue-600 px-6 py-2 text-sm font-medium text-white hover:bg-blue-700">
            Ir para início
          </button>
        </div>
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────────────────────
  if (success) {
    const needsConfirmation = !currentUserEmail;
    return (
      <div className={shellClassName}>
        <div className={cardClassName + ' text-center'}>
          <div className="mb-4 text-3xl">{needsConfirmation ? '📧' : '✅'}</div>
          <h1 className="mb-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
            {needsConfirmation ? 'Verifique seu e-mail' : 'Convite Aceito!'}
          </h1>
          <p className="text-sm text-slate-500">
            {needsConfirmation
              ? `Enviamos um link de confirmação para ${invitation?.email}. Após confirmar, você poderá acessar o Workflow Manager.`
              : 'Redirecionando...'}
          </p>
        </div>
      </div>
    );
  }

  const inviteEmail = invitation?.email ?? '';
  const isCorrectUser = currentUserEmail === inviteEmail;
  const isWrongUser = !!currentUserEmail && !isCorrectUser;

  // ── Wrong user logged in ─────────────────────────────────────────────────
  if (isWrongUser) {
    return (
      <div className={shellClassName}>
        <div className={cardClassName + ' text-center'}>
          <div className="mb-4 text-3xl">🔄</div>
          <h1 className="mb-3 text-base font-semibold text-slate-800 dark:text-slate-100">Conta diferente</h1>
          <p className="mb-1 text-sm text-slate-500">
            Este convite é para <strong className="text-slate-700 dark:text-slate-200">{inviteEmail}</strong>.
          </p>
          <p className="mb-6 text-xs text-slate-400">
            Você está logado como <strong>{currentUserEmail}</strong>.
          </p>
          <button
            onClick={handleSwitchAccount}
            className="w-full rounded bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
          >
            Sair e usar {inviteEmail}
          </button>
          <button
            onClick={() => navigate('/')}
            className="mt-2 w-full rounded border border-slate-300 px-6 py-2 text-sm text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-400"
          >
            Cancelar
          </button>
        </div>
      </div>
    );
  }

  // ── Correct user already logged in ──────────────────────────────────────
  if (isCorrectUser) {
    return (
      <div className={shellClassName}>
        <div className={cardClassName + ' text-center'}>
          <div className="mb-4 text-3xl">📩</div>
          <h1 className="mb-1 text-base font-semibold text-slate-800 dark:text-slate-100">Você foi convidado!</h1>
          <p className="mb-1 text-sm text-slate-500">
            Um administrador convidou você para a equipe{' '}
            <strong className="text-slate-700 dark:text-slate-200">{invitation?.organization_name ?? ''}</strong>.
          </p>
          <p className="mb-6 text-xs text-slate-400">Função: <strong>{invitation?.role}</strong></p>

          {error && (
            <div className="mb-4 rounded bg-red-50 p-2 text-xs text-red-600 dark:bg-red-900/20 dark:text-red-400">{error}</div>
          )}

          <button
            onClick={handleAcceptAsCurrentUser}
            disabled={submitting}
            className="w-full rounded bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Aceitando...' : 'Aceitar Convite'}
          </button>
          <p className="mt-4 text-[10px] text-slate-400">
            Expira em {invitation ? new Date(invitation.expires_at).toLocaleDateString('pt-BR') : '—'}
          </p>
        </div>
      </div>
    );
  }

  // ── Not logged in — show signup/login form ───────────────────────────────
  return (
    <div className={shellClassName}>
      <div className={cardClassName}>
        <div className="mb-5 text-center">
          <div className="mb-3 text-3xl">📩</div>
          <h1 className="text-base font-semibold text-slate-800 dark:text-slate-100">Você foi convidado!</h1>
          <p className="mt-1 text-sm text-slate-500">
            Para acessar a equipe <strong className="text-slate-700 dark:text-slate-200">{invitation?.organization_name ?? ''}</strong>{' '}
            como <strong>{invitation?.role}</strong>.
          </p>
        </div>

        {/* Mode toggle */}
        <div className="mb-5 flex rounded border border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={() => { setAuthMode('signup'); setError(''); }}
            className={`flex-1 rounded-l py-2 text-xs font-medium transition-colors ${authMode === 'signup' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
          >
            Criar conta
          </button>
          <button
            type="button"
            onClick={() => { setAuthMode('login'); setError(''); }}
            className={`flex-1 rounded-r py-2 text-xs font-medium transition-colors ${authMode === 'login' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
          >
            Já tenho conta
          </button>
        </div>

        {/* Email (read-only, from invitation) */}
        <div className="mb-3">
          <label className={labelClassName}>E-mail</label>
          <input
            type="email"
            value={inviteEmail}
            readOnly
            className={inputClassName + ' cursor-not-allowed opacity-60'}
          />
        </div>

        {authMode === 'signup' ? (
          <form onSubmit={handleSignUp} className="space-y-3">
            <div>
              <label className={labelClassName}>Nome completo</label>
              <input
                type="text"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                placeholder="Seu nome"
                required
                className={inputClassName}
              />
            </div>
            <div>
              <label className={labelClassName}>Senha</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                required
                className={inputClassName}
              />
            </div>
            <div>
              <label className={labelClassName}>Confirmar senha</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Repita a senha"
                required
                className={inputClassName}
              />
            </div>

            {error && (
              <div className="rounded bg-red-50 p-2 text-xs text-red-600 dark:bg-red-900/20 dark:text-red-400">{error}</div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-1 w-full rounded bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Criando conta...' : 'Criar conta e aceitar convite'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleLogin} className="space-y-3">
            <div>
              <label className={labelClassName}>Senha</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Sua senha"
                required
                className={inputClassName}
              />
            </div>

            {error && (
              <div className="rounded bg-red-50 p-2 text-xs text-red-600 dark:bg-red-900/20 dark:text-red-400">{error}</div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-1 w-full rounded bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Entrando...' : 'Entrar e aceitar convite'}
            </button>
          </form>
        )}

        <p className="mt-4 text-center text-[10px] text-slate-400">
          Convite expira em {invitation ? new Date(invitation.expires_at).toLocaleDateString('pt-BR') : '—'}
        </p>
      </div>
    </div>
  );
}
